import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchEmployeesForSettlementApi,
  fetchPayrollForMonthApi,
  fetchPayrollListForMonthApi,
  saveSettlementApi,
} from "../../api/payroll.api";
import { EmployeeDetails } from "../../models/employeeDetails.model";

interface PayrollState {
  loading: boolean;
  data: Record<string, any> | null;
  error: string | null;
  allUsersPayroll: any[] | null;
  settlementEmployeesList: {
    userId: string;
    aadharName: string;
    employeeNo: string;
  }[];
  fnfLoading: boolean;
  fnfData: any[];
  fnfError: string | null;
  settlementEmployees: EmployeeDetails[];
}

const initialState: PayrollState = {
  loading: false,
  data: null,
  error: null,
  allUsersPayroll: null,
  settlementEmployeesList: [],
  fnfLoading: false,
  fnfData: [],
  fnfError: null,
  settlementEmployees: [],
};

export const fetchPayrollForMonth = createAsyncThunk(
  "payroll/fetchForMonth",
  async (
    { userId, userIds, month, year }: { userId?: string; userIds?: string[]; month: number; year: number },
    { rejectWithValue }
  ) => {
    try {
      return await fetchPayrollForMonthApi(userId, userIds, month, year);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch payroll data");
    }
  }
);

export const fetchPayrollListForMonth = createAsyncThunk(
  "payroll/fetchListForMonth",
  async (
    { month, year }: { month: number; year: number },
    { rejectWithValue }
  ) => {
    try {
      return await fetchPayrollListForMonthApi(month, year);
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const fetchEmployeesForSettlement = createAsyncThunk(
  "employee/fetchForSettlement",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchEmployeesForSettlementApi();
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const saveSettlement = createAsyncThunk(
  "payroll/saveSettlement",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const finalPayload = {
        ...payload,
        settlementDate: payload.settlementDate || new Date().toISOString(),
      };
      const response = await saveSettlementApi(finalPayload);

      const year = payload.year || new Date(payload.settlementDate || new Date()).getFullYear();
      const month = payload.payrollMonth || new Date(payload.settlementDate || new Date()).getMonth() + 1;

      console.log(`Re-fetching payroll list for month: ${month}, year: ${year}`);

      await dispatch(fetchPayrollListForMonth({ month, year })).unwrap();

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const payrollSlice = createSlice({
  name: "payroll",
  initialState,
  reducers: {
    resetPayrollState: (state) => {
      state.loading = false;
      state.data = null;
      state.error = null;
      state.allUsersPayroll = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayrollForMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayrollForMonth.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          if (action.payload.length === 1 && action.payload[0].payroll) {
            state.data = action.payload[0].payroll; // Single-user response
          } else if (action.payload.length > 1) {
            state.allUsersPayroll = action.payload; // Batch response
          }
        }
      })
      .addCase(fetchPayrollForMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.allUsersPayroll = null;
        state.data = null;
      })
      .addCase(fetchPayrollListForMonth.pending, (state) => {
        state.fnfLoading = true;
        state.fnfError = null;
      })
      .addCase(fetchPayrollListForMonth.fulfilled, (state, action) => {
        state.fnfLoading = false;
        state.fnfData = action.payload || [];
      })
      .addCase(fetchPayrollListForMonth.rejected, (state, action) => {
        state.fnfLoading = false;
        state.fnfError = action.payload as string;
      })
      .addCase(fetchEmployeesForSettlement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeesForSettlement.fulfilled, (state, action) => {
        const payload = action.payload || [];
        state.settlementEmployees = payload.filter(
          (emp: { settled: any }) => !emp.settled
        );
        state.settlementEmployeesList = state.settlementEmployees.map(
          (emp: EmployeeDetails) => ({
            userId: emp.userId,
            aadharName: emp.aadharName || "Unknown",
            employeeNo: emp.employeeNo || "N/A",
          })
        );
        state.loading = false;
      })
      .addCase(fetchEmployeesForSettlement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(saveSettlement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveSettlement.fulfilled, (state, action) => {
        state.loading = false;
        const saved = action.payload?.data;
        if (!saved) return;
        state.settlementEmployees = state.settlementEmployees.filter(
          (emp) => emp.userId !== saved.userId
        );
        state.settlementEmployeesList = state.settlementEmployees.map(
          (emp) => ({
            userId: emp.userId,
            aadharName: emp.aadharName || "Unknown",
            employeeNo: emp.employeeNo || "N/A",
          })
        );
      })
      .addCase(saveSettlement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetPayrollState } = payrollSlice.actions;
export default payrollSlice.reducer;